<template lang="html">
  <card>
    <h2 class="title">{{ $t("landingPage.chargingPricesCard.title") }}</h2>

    <ul class="prices">
      <li class="price-item">
        <span class="price-title">{{
          $t("landingPage.chargingPricesCard.dcChargerTitle")
        }}</span>
        <span class="price-value"
          >{{ formatPrice(dcChargerPrice) }} {{ currencyLabel }} /
          {{ translatePricingUnit(dcChargerBaseOfCalculation) }}</span
        >
      </li>
      <li class="price-item">
        <span class="price-title">{{
          $t("landingPage.chargingPricesCard.rapidChargerTitle")
        }}</span>
        <span class="price-value"
          >{{ formatPrice(rapidChargerPrice) }} {{ currencyLabel }} /
          {{ translatePricingUnit(rapidChargerBaseOfCalculation) }}</span
        >
      </li>
      <li class="price-item">
        <span class="price-title">{{
          $t("landingPage.chargingPricesCard.acChargerTitle")
        }}</span>
        <span class="price-value"
          >{{ formatPrice(acChargerPrice) }} {{ currencyLabel }} /
          {{ translatePricingUnit(acChargerBaseOfCalculation) }}</span
        >
      </li>
    </ul>

    <div v-if="guestCommission > 0" class="extra-fee">
      +
      <strong>{{ formatPrice(guestCommission) }} {{ currencyLabel }}</strong>
      {{ $t("landingPage.chargingPricesCard.guestCommission") }}
    </div>
    <div
      v-else-if="!hideRegistrationCharge && registrationCharge > 0"
      class="extra-fee"
    >
      +
      <strong>{{ formatPrice(registrationCharge) }} {{ currencyLabel }}</strong>
      {{ $t("landingPage.chargingPricesCard.registrationCharge") }}
    </div>
    <div v-if="typeof prepayAmount === 'number'" class="prepay-amount">
      <span class="price-title">{{
        $t("landingPage.chargingPricesCard.prepayAmountTitle")
      }}</span>
      <span class="price-value"
        >{{ formatPrice(prepayAmount) }} {{ currencyLabel }} /
        {{ $t("landingPage.chargingPricesCard.occasion") }}</span
      >
    </div>
    <div class="additional-text">
      {{
        $t("landingPage.chargingPricesCard.pricesValidFrom", {
          date: format.formatDate(validFrom)
        })
      }}
    </div>
    <div
      v-if="
        (!guestCommission || guestCommission === 0) &&
        !hideRegistrationCharge &&
        registrationCharge > 0
      "
      class="additional-text"
    >
      {{
        $t("landingPage.chargingPricesCard.registrationChargeAdditionalText")
      }}
    </div>
  </card>
</template>

<script lang="ts" setup>
import Card from "./Card.vue"
import { PriceService } from "@/services/price-service"
import config from "@/config"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import format from "@/utils/format"

interface ChargerPricesProps {
  acChargerBaseOfCalculation: string
  acChargerPrice: number
  currency: string
  dcChargerBaseOfCalculation: string
  dcChargerPrice: number
  prepayAmount?: number
  rapidChargerBaseOfCalculation: string
  rapidChargerPrice: number
  validFrom: string
  hideRegistrationCharge: boolean
  guestCommission?: number
  registrationCharge?: number
}

const props = withDefaults(defineProps<ChargerPricesProps>(), {
  acChargerBaseOfCalculation: "",
  acChargerPrice: 0,
  currency: "",
  dcChargerBaseOfCalculation: "",
  dcChargerPrice: 0,
  prepayAmount: undefined,
  rapidChargerBaseOfCalculation: "",
  rapidChargerPrice: 0,
  validFrom: "",
  hideRegistrationCharge: false,
  guestCommission: undefined,
  registrationCharge: undefined
})

const i18n = useI18n()

const currencyLabel = computed(() => {
  return config.getCurrencyLabel(props.currency)
})

const formatPrice = (value: number) => {
  return PriceService.formatPrice(value, i18n)
}

const translatePricingUnit = (unit: string) => {
  return PriceService.translatePricingUnit(unit, i18n)
}
</script>

<style scoped lang="scss">
.title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.price-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.price-value {
  font-weight: 700;
  flex-shrink: 0;
  padding-left: 8px;
}

.price-item {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.prices {
  margin-bottom: 12px;
}

.additional-text {
  font-style: italic;
  font-size: 14px;
}

.prepay-amount {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 16px;
  margin-top: 20px;
}

.extra-fee {
  font-size: 14px;
}
</style>
